/**
 * Hooks for fetching user files and folders
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { filesApi } from '@api/endpoints/files'
import type {
  GetFilesParams,
  UpdateFileRequest,
  FileCategory,
  CreateFolderRequest,
  UpdateFolderRequest,
  MoveFilesRequest,
} from '@api/types/files'

export const FILE_QUERY_KEYS = {
  all: ['files'] as const,
  list: (params?: GetFilesParams) => [...FILE_QUERY_KEYS.all, 'list', params] as const,
  detail: (id: string) => [...FILE_QUERY_KEYS.all, 'detail', id] as const,
  usages: (id: string) => [...FILE_QUERY_KEYS.all, 'usages', id] as const,
  folders: () => [...FILE_QUERY_KEYS.all, 'folders'] as const,
  folderTree: () => [...FILE_QUERY_KEYS.all, 'folderTree'] as const,
  quota: () => [...FILE_QUERY_KEYS.all, 'quota'] as const,
}

export function useFiles(params?: GetFilesParams) {
  return useQuery({
    queryKey: FILE_QUERY_KEYS.list(params),
    queryFn: () => filesApi.getFiles(params),
  })
}

export function useFile(fileId: string) {
  return useQuery({
    queryKey: FILE_QUERY_KEYS.detail(fileId),
    queryFn: () => filesApi.getFile(fileId),
    enabled: !!fileId,
  })
}

export function useFileUsages(fileId: string) {
  return useQuery({
    queryKey: FILE_QUERY_KEYS.usages(fileId),
    queryFn: () => filesApi.getFileUsages(fileId),
    enabled: !!fileId,
  })
}

export function useFolders() {
  return useQuery({
    queryKey: FILE_QUERY_KEYS.folders(),
    queryFn: () => filesApi.getFolders(),
  })
}

export function useFolderTree() {
  return useQuery({
    queryKey: FILE_QUERY_KEYS.folderTree(),
    queryFn: () => filesApi.getFolderTree(),
  })
}

export function useStorageQuota() {
  return useQuery({
    queryKey: FILE_QUERY_KEYS.quota(),
    queryFn: () => filesApi.getQuota(),
  })
}

export function useUpdateFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ fileId, data }: { fileId: string; data: UpdateFileRequest }) =>
      filesApi.updateFile(fileId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.all })
      queryClient.setQueryData(FILE_QUERY_KEYS.detail(data.id), data)
    },
  })
}

export function useDeleteFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ fileId, force }: { fileId: string; force?: boolean }) =>
      filesApi.deleteFile(fileId, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.all })
    },
  })
}

export function useToggleFavorite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileId: string) => filesApi.toggleFavorite(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.all })
    },
  })
}

export function useUploadFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      file,
      options,
    }: {
      file: File
      options?: {
        folderId?: string
        category?: FileCategory
        displayName?: string
        description?: string
        tags?: string[]
      }
    }) => filesApi.uploadFile(file, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.all })
    },
  })
}

// ===================
// FOLDER MUTATIONS
// ===================

export function useCreateFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateFolderRequest) => filesApi.createFolder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.folders() })
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.folderTree() })
    },
  })
}

export function useUpdateFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ folderId, data }: { folderId: string; data: UpdateFolderRequest }) =>
      filesApi.updateFolder(folderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.folders() })
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.folderTree() })
    },
  })
}

export function useDeleteFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (folderId: string) => filesApi.deleteFolder(folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.folders() })
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.folderTree() })
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.all })
    },
  })
}

export function useMoveFiles() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: MoveFilesRequest) => filesApi.moveFiles(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILE_QUERY_KEYS.all })
    },
  })
}
